//=============================================================================
//                       <--- Developed By Icon Team --->
//=============================================================================

// npm packages
import React, { useState, useCallback, useEffect, useContext } from "react";
import * as moment from "moment";
import * as XLSX from "xlsx"; // npm install xlsx
import { toast } from "react-toastify";

// custom pages
import { styles } from "./styles";
import { GrowthBonusMailTracker } from "./config/config";
import NoData from "components/Table/NoData";
import Loader from "components/Loader/Loader";
import Context from "context/context";
import report from "apis/Report/Report";

// material-ui
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";

// material-ui icons
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useTranslation } from "react-i18next";

const GlobalCss = withStyles({
    "@global": {
        ".MuiDialog-scrollPaper": {},
        ".MuiDialog-paperWidthSm": {
            overflow: "hidden",
            maxWidth: "none",
        },
        ".MuiDialog-paper ": {},
    },
})(() => null);

// ---------------------------------------------------------------------------
// Excel helpers
// ---------------------------------------------------------------------------

// role_id 1 = Customer, role_id 7 = FSC (same mapping as the backend query)
const getRole = (row) => {
    if (row?.customer_role === "Customer" || Number(row?.role_id) === 1) return "Customer";
    if (row?.customer_role === "FSC" || Number(row?.role_id) === 7) return "FSC";
    return "Other";
};

const formatCell = (value) => {
    if (value === null || value === undefined) return "";
    if (value === true) return "True";
    if (value === false) return "False";
    return value;
};

// Builds one worksheet from rows + the selected {label, key} headers
const buildSheet = (rows, headers) => {
    const aoa = [
        headers.map((h) => h.label),
        ...rows.map((r) => headers.map((h) => formatCell(r?.[h.key]))),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // auto column width (based on the first 200 rows, capped at 40 chars)
    ws["!cols"] = headers.map((h, i) => ({
        wch:
            Math.min(
                40,
                Math.max(12, ...aoa.slice(0, 200).map((row) => String(row[i] ?? "").length))
            ) + 2,
    }));
    return ws;
};

// Creates an .xlsx with a "Customer" tab and an "FSC" tab and downloads it
const exportToExcel = (data = [], headers = []) => {
    const customerRows = data.filter((r) => getRole(r) === "Customer");
    const fscRows = data.filter((r) => getRole(r) === "FSC");
    const otherRows = data.filter((r) => getRole(r) === "Other");

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildSheet(customerRows, headers), "Customer");
    XLSX.utils.book_append_sheet(wb, buildSheet(fscRows, headers), "FSC");

    // only added when some rows have a role other than Customer/FSC,
    // so no data is silently dropped from the export
    if (otherRows.length > 0) {
        XLSX.utils.book_append_sheet(wb, buildSheet(otherRows, headers), "Other");
    }

    XLSX.writeFile(
        wb,
        `GrowthBonusMailTracker_${moment(new Date()).format("MM-DD-YYYY_hh-mm-A")}.xlsx`
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UnlockEarnReport(props) {
    const { state, dispatch } = useContext(Context);

    const { page, setDisableDropdown } = props;

    const { growthBonusMailTrackerReport } = report();
    const classes = styles();
    const { t } = useTranslation();

    const [states, setstates] = useState({
        columns: [],
        selectedColumns: [],
        selectedFields: [],
        selectedHeaders: [],
        dialog: false,
        data: [],
        response: false,
        startDate: null,
        endDate: null,
        dateApplied: false,
        filterColumn: "",
        filterValue: "",
        filterApplied: false,
        groupBy: "",
        groupByApplied: false,
        pLoading: false,
        eLoading: false,
        errorDialog: false,
        errorCount: "",
    });

    const setStates = useCallback((newState) => {
        setstates((states) => ({ ...states, ...newState }));
    }, []);

    // setting checkboxes
    useEffect(() => {
        let label = GrowthBonusMailTracker?.columns?.map((c) => c.label);
        setStates({ columns: label, selectedColumns: label });
    }, []);

    // Fetches the report. On success returns { data, headers } so the caller
    // can export immediately without waiting for React state to update.
    const handleGetBurn = async () => {
        try {
            setDisableDropdown(true);
            let selectedColumnKeys = [];
            let selectedHeaders = [];

            GrowthBonusMailTracker.columns.forEach((c) => {
                if (states.selectedColumns?.includes(c.label)) {
                    selectedColumnKeys = [...selectedColumnKeys, c.key];
                    selectedHeaders = [...selectedHeaders, c];
                }
            });

            setStates({
                selectedFields: selectedColumnKeys,
                selectedHeaders: selectedHeaders,
            });

            let values = {
                fields: selectedColumnKeys,
            };
            if (states.dateApplied) {
                values = {
                    ...values,
                    startDate: moment(states.startDate).format("MM/DD/YYYY"),
                    endDate: moment(states.endDate).format("MM/DD/YYYY"),
                };
            }
            if (states.filterApplied) {
                values = {
                    ...values,
                    filterBy: { [states.filterColumn]: [states.filterValue] },
                };
            }
            if (states.groupByApplied) {
                values = { ...values, groupBy: states.groupBy };
            }

            let res = await growthBonusMailTrackerReport(values);

            if (res && res?.type === "ERROR") {
                dispatch({ type: "SET_REPORT_RES_COUNT", payload: 0 });
                setStates({
                    pLoading: false,
                    eLoading: false,
                    errorDialog: true,
                    errorCount: res?.count,
                });
                setDisableDropdown(false);
                return false;
            }
            if (res && res?.data) {
                dispatch({ type: "SET_REPORT_RES_COUNT", payload: 0 });
                setStates({
                    data: res.data,
                    response: true,
                    pLoading: false,
                    eLoading: false,
                });
                setDisableDropdown(false);
                if (res.data.length === 0) {
                    toast.success(t("No Data Found"), { toastId: "no-report-date" });
                    return false;
                }
                return { data: res.data, headers: selectedHeaders };
            }
            return false;
        } catch (err) {
            console.log(err);
            setStates({ pLoading: false, eLoading: false });
            setDisableDropdown(false);
            return false;
        }
    };

    const pointsFormat = (value) => {
        if (value) {
            var nfObject = new Intl.NumberFormat("en-US");
            var points = nfObject.format(value);
            return points;
        }
        return "";
    };

    const pointsDecimalFormat = (value) => {
        if (value) {
            var points = value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return points;
        }
        return "";
    };

    return (
        <div>
            <GlobalCss />
            <div className={classes.headText}>
                {states.dateApplied && (
                    <React.Fragment>
                        <span className={classes.appliedSpan}>
                            <CheckCircleIcon />
                        </span>
                        <span
                            className={classes.removeSpan}
                            onClick={() => {
                                setStates({
                                    dateApplied: false,
                                    startDate: null,
                                    endDate: null,
                                });
                            }}
                        >
                            {"Remove"}
                        </span>
                    </React.Fragment>
                )}
            </div>

            <div style={{ color: "gray", padding: 10 }}>
                {`*Max  file count can be exported ${pointsFormat(800000)}`}
            </div>

            <div className={classes.exportAndPreview}>
                <Button
                    className={classes.exportBtn}
                    onClick={async () => {
                        if (
                            (states.startDate && !states.endDate) ||
                            (!states.startDate && states.endDate)
                        ) {
                            toast.error("Please select the both Start-date and End-date values", {
                                toastId: "empty-fields",
                            });
                            return;
                        }
                        if (
                            states.startDate?.toString() === "Invalid Date" &&
                            states.endDate?.toString() === "Invalid Date"
                        ) {
                            toast.error("Invalid date.", { toastId: "invalid-date" });
                            setStates({ startDate: new Date(), endDate: new Date() });
                            return;
                        } else if (states.startDate?.toString() === "Invalid Date") {
                            toast.error("Invalid date.", { toastId: "invalid-s-date" });
                            setStates({ startDate: new Date() });
                            return;
                        } else if (states.endDate?.toString() === "Invalid Date") {
                            toast.error("Invalid date.", { toastId: "invalid-e-date" });
                            setStates({ endDate: new Date() });
                            return;
                        }

                        setStates({ response: false, data: [], eLoading: true });
                        const result = await handleGetBurn();
                        if (result) {
                            // Customer tab + FSC tab
                            exportToExcel(result.data, result.headers);
                        }
                    }}
                    disabled={states.eLoading || states.pLoading}
                >
                    {states.eLoading ? (
                        <React.Fragment>
                            <CircularProgress
                                style={{ height: 25, width: 25, color: "#fff" }}
                                variant="determinate"
                                value={state.reportResCount}
                            />
                            &nbsp;
                            <span style={{ color: "#fff", fontWeight: "bold" }}>
                                {state.reportResCount == 0 ? (
                                    <div className="snippet">
                                        <div className="stage">
                                            <div className="dot-typing-e"></div>
                                        </div>
                                    </div>
                                ) : (
                                    `${state.reportResCount}%`
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        "Export Excel"
                    )}
                </Button>
                {(states.eLoading || states.pLoading) && (
                    <div style={{ color: "red", marginLeft: 10 }}>
                        *Please wait don't reload the page, system is processing
                    </div>
                )}
            </div>

            {/* Preview dialog (only opens if you re-enable a Preview button that sets dialog: true) */}
            <Dialog
                onClose={() => {
                    setStates({ dialog: false });
                }}
                aria-labelledby="simple-dialog-title"
                open={states.dialog}
            >
                <div className={classes.mainDiv}>
                    <Grid container spacing={0}>
                        <Grid item xs={12} className={classes.gridOne}>
                            {page + " " + "Report"}
                            <HighlightOffIcon
                                className={classes.gridTwo}
                                onClick={() => {
                                    setStates({ dialog: false });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.gridFive}>
                            <span className={classes.gridThreeSpan}>Generated Date : </span>
                            <span>{moment(new Date()).format("MM-DD-YYYY")}</span>
                        </Grid>
                    </Grid>
                    {states.response ? (
                        states.data && states.data.length > 0 ? (
                            <TableContainer className={classes.tableContainer}>
                                <Table
                                    className={classes.table}
                                    aria-labelledby="tableTitle"
                                    size={"medium"}
                                    aria-label="enhanced table"
                                >
                                    <TableHead className={classes.tableHead}>
                                        <TableRow>
                                            {states.selectedColumns?.map((h, i) => (
                                                <TableCell key={i} className={classes.tableHeading}>
                                                    <div>{h}</div>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {states.data?.slice(0, 10)?.map((p, i) => (
                                            <TableRow role="checkbox" tabIndex={-1} key={i}>
                                                {states.selectedFields.map((s) => (
                                                    <TableCell
                                                        key={s}
                                                        align="left"
                                                        className={classes.rowCell}
                                                    >
                                                        {p?.[s] === true
                                                            ? "True"
                                                            : p?.[s] === false
                                                            ? "False"
                                                            : s === "extended_sales_amount"
                                                            ? pointsDecimalFormat(p?.[s])
                                                            : s === "points"
                                                            ? pointsFormat(p?.[s])
                                                            : p?.[s]}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <NoData />
                        )
                    ) : (
                        <Loader Loading={!states?.response}></Loader>
                    )}
                </div>
                {states.data && states.data.length > 0 && (
                    <Grid container spacing={0}>
                        <Grid item xs={12} className={classes.gridFour}>
                            <Button
                                className={classes.previewBtn}
                                onClick={() => {
                                    setStates({ dialog: false });
                                }}
                            >
                                {"Cancel"}
                            </Button>
                            <Button
                                className={classes.exportBtn}
                                onClick={() => {
                                    setStates({ dialog: false });
                                    exportToExcel(states.data, states.selectedHeaders);
                                }}
                            >
                                {"Export Excel"}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Dialog>

            <Dialog
                aria-labelledby="simple-dialog-title"
                open={states?.errorDialog}
                style={{ fontFamily: "Segoe-ui" }}
            >
                <div className={classes.dialogParentDiv}>
                    <div style={{ display: "flow-root" }}>
                        <span style={{ float: "right" }}>
                            <HighlightOffIcon
                                style={{
                                    verticalAlign: "-webkit-baseline-middle",
                                    cursor: "pointer",
                                    color: "red",
                                }}
                                onClick={() => {
                                    setStates({ errorDialog: false });
                                }}
                            />
                        </span>
                    </div>
                    <div className={classes.parentText}>
                        {"The date range has fetched"} {states.errorCount} {"records."} <br />
                        {"Due to Excel/CSV limitation we can only allow for 800,000 recordsfor export."}{" "}
                        <br />
                        {"Please add additional filters to limit your record count to less than 800,000."}
                    </div>
                </div>
            </Dialog>
        </div>
    );
}